// eslint-disable-next-line no-restricted-imports
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { useSession } from 'src/contexts/SessionContext';
import { Appointment } from 'src/models/appointment/_types';
import { formatCurrency } from 'src/utils/numberFormatter';

import { Visit, VisitStatus } from '../../models/visit/_types';
import { usePermissionCheck } from '../usePermissionCheck';

type VisitStatusDropdownItemsProps = {
  visit: Visit;
  appointment: Appointment;
  updateVisitStatus: (
    visit: Visit,
    status: VisitStatus,
    options?: { chargeNoshow?: boolean; chargeCancellation?: boolean }
  ) => void;
  setIsOpen: (isOpen: boolean) => void;
  isTrainerView?: boolean;
  includeReset?: boolean;
};

export const VisitStatusDropdownItems = ({
  visit,
  appointment,
  updateVisitStatus,
  setIsOpen,
  isTrainerView = true,
  includeReset = true,
}: VisitStatusDropdownItemsProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'common.labels' });
  const { t: tMessages } = useTranslation('translation', {
    keyPrefix: 'common.messages',
  });
  const { canCancelVisit, canEarlyCancelVisit } = usePermissionCheck();
  const { session } = useSession();

  const [confirmFeeProps, confirm] = useConfirmDialogComponent();

  // Trainers always see Confirm/Complete. Clients match Ember My Visits:
  // only when the platform has not disabled client check-in.
  const showConfirmAndCheckIn = isTrainerView || !session?.disableClientCheckin;

  const updateVisitStatusAndClose = (
    visit: Visit,
    status: VisitStatus,
    options?: { chargeNoshow?: boolean; chargeCancellation?: boolean }
  ) => {
    updateVisitStatus(visit, status, options);
    setIsOpen(false);
  };

  const handleNoshowFee = async () => {
    if (
      await confirm(
        tMessages(
          'Are you sure you want to mark this session as no show AND charge a fee of {{fee}}?',
          {
            fee: formatCurrency(appointment.noshowFee),
          }
        )
      )
    ) {
      updateVisitStatusAndClose(visit, VisitStatus.NoShowed, {
        chargeNoshow: true,
      });
    }
    setIsOpen(false);
  };

  const handleCancellationFee = async () => {
    if (
      await confirm(
        tMessages(
          'Are you sure you want to mark this session as late cancel AND charge a fee of {{fee}}?',
          {
            fee: formatCurrency(appointment.cancellationFee, {
              currency: session?.defaultCurrency,
            }),
          }
        )
      )
    ) {
      updateVisitStatusAndClose(visit, VisitStatus.LateCancelled, {
        chargeCancellation: true,
      });
    }

    setIsOpen(false);
  };

  return (
    <>
      {showConfirmAndCheckIn && (
        <>
          <MenuItem
            onClick={() => {
              updateVisitStatusAndClose(visit, VisitStatus.Registered);
            }}
          >
            {t('Confirm')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              updateVisitStatusAndClose(visit, VisitStatus.Completed);
            }}
          >
            {t('Complete / Check-In')}
          </MenuItem>
        </>
      )}
      {isTrainerView && (
        <>
          {canCancelVisit && (
            <>
              <MenuItem
                onClick={() => {
                  updateVisitStatusAndClose(
                    visit,
                    canEarlyCancelVisit
                      ? VisitStatus.Cancelled
                      : VisitStatus.CancelOrLateCancelled
                  );
                }}
              >
                {canEarlyCancelVisit ? t('Early Cancel') : t('Mark Cancelled')}
              </MenuItem>
              {appointment.cancellationFee && !visit.lateFee ? (
                <>
                  <MenuItem onClick={handleCancellationFee}>
                    {t('Late Cancel with Fee')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      updateVisitStatusAndClose(
                        visit,
                        VisitStatus.LateCancelled,
                        {
                          chargeCancellation: false,
                        }
                      );
                    }}
                  >
                    {t('Late Cancel without Fee')}
                  </MenuItem>
                </>
              ) : (
                canEarlyCancelVisit && (
                  <MenuItem
                    onClick={() => {
                      updateVisitStatusAndClose(
                        visit,
                        VisitStatus.LateCancelled
                      );
                    }}
                  >
                    {t('Late Cancel')}
                  </MenuItem>
                )
              )}
            </>
          )}
          {appointment.noshowFee && !visit.noshowFee ? (
            <>
              <MenuItem onClick={handleNoshowFee}>
                {t('No Show with Fee')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  updateVisitStatusAndClose(visit, VisitStatus.NoShowed, {
                    chargeNoshow: false,
                  });
                }}
              >
                {t('No Show without Fee')}
              </MenuItem>
            </>
          ) : (
            <MenuItem
              onClick={() => {
                updateVisitStatusAndClose(visit, VisitStatus.NoShowed);
              }}
            >
              {t('No Show')}
            </MenuItem>
          )}
          {includeReset ? (
            <MenuItem
              onClick={() => {
                updateVisitStatusAndClose(visit, VisitStatus.Reset);
              }}
            >
              {t('Reset Status')}
            </MenuItem>
          ) : null}
        </>
      )}
      <ConfirmDialog {...confirmFeeProps} title={t('Charge Fee?')} />
    </>
  );
};
